import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Send, ScrollText, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/** Resend email notifications — new-order alerts + daily pending summary.
 * API key is stored server-side (settings.email_notify_config); the UI only
 * ever sees a masked version. */
export const EmailNotifySection = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logRows, setLogRows] = useState(null);
  const [logBusy, setLogBusy] = useState(false);
  const [preview, setPreview] = useState(null); // full log entry with html

  const openPreview = async (id) => {
    try {
      const res = await axios.get(`${API}/api/notifications/email-log/${id}`, { headers: authHeader() });
      setPreview(res.data);
    } catch {
      toast.error("Failed to load email content");
    }
  };

  const fetchLog = async () => {
    setLogBusy(true);
    try {
      const res = await axios.get(`${API}/api/notifications/email-log?limit=100`, { headers: authHeader() });
      setLogRows(res.data || []);
    } catch {
      toast.error("Failed to load email log");
    } finally {
      setLogBusy(false);
    }
  };
  const [form, setForm] = useState({
    recipient: "", from_name: "", reply_to: "", enabled: true,
    daily_summary_enabled: true, daily_summary_time: "07:00",
    customer_emails_enabled: false, customer_order_enabled: true,
    customer_delivered_enabled: true, customer_invoice_enabled: true,
  });

  const fetchCfg = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notifications/email-config`, { headers: authHeader() });
      setCfg(res.data);
      setForm((f) => ({
        ...f,
        recipient: res.data.recipient,
        from_name: res.data.from_name || "",
        reply_to: res.data.reply_to || "",
        enabled: res.data.enabled,
        daily_summary_enabled: res.data.daily_summary_enabled,
        daily_summary_time: res.data.daily_summary_time,
        customer_emails_enabled: res.data.customer_emails_enabled,
        customer_order_enabled: res.data.customer_order_enabled,
        customer_delivered_enabled: res.data.customer_delivered_enabled,
        customer_invoice_enabled: res.data.customer_invoice_enabled,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load email config");
    }
  }, []);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.recipient.trim()) { toast.error("Recipient email is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/notifications/email-config`, form, { headers: authHeader() });
      toast.success("Email notification settings saved");
      await fetchCfg();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/api/notifications/email-test`, {}, { headers: authHeader() });
      toast.success("Test email sent — check the inbox");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="p-5" data-testid="email-notify-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-brand-navy" />
          <h3 className="font-semibold text-brand-navy">Email notifications (Resend)</h3>
        </div>
        <Button
          size="sm"
          onClick={handleTest}
          disabled={testing}
          data-testid="email-notify-test-btn"
          className="bg-brand-navy hover:bg-brand-navy-hover text-white"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          <span className="ml-1.5">Send test</span>
        </Button>
      </div>

      <div className="text-sm text-muted-foreground mb-4 space-y-1">
        <p>
          Powered by <strong>Emergent managed email</strong> — no Resend account, API key or domain
          verification needed. Emails can be delivered to any recipient. The sender address is managed
          by the platform; you control the <strong>sender name</strong> and <strong>reply-to</strong>.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="notify-from-name">Sender name (blank = Company name)</Label>
            <Input
              id="notify-from-name"
              placeholder={cfg?.effective_from_name || "Company name from Company Info"}
              value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              data-testid="email-notify-fromname-input"
            />
          </div>
          <div>
            <Label htmlFor="notify-recipient">Admin alert recipient</Label>
            <Input
              id="notify-recipient" type="email" required
              placeholder="you@example.com"
              value={form.recipient}
              onChange={(e) => setForm({ ...form, recipient: e.target.value })}
              data-testid="email-notify-recipient-input"
            />
          </div>
          <div>
            <Label htmlFor="notify-reply-to">Reply-to (customer replies go here)</Label>
            <Input
              id="notify-reply-to" type="email"
              placeholder="wei@drrt.com.au"
              value={form.reply_to}
              onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
              data-testid="email-notify-replyto-input"
            />
          </div>
          <div>
            <Label htmlFor="summary-time">Daily summary time (Brisbane)</Label>
            <Input
              id="summary-time" type="time"
              value={form.daily_summary_time}
              onChange={(e) => setForm({ ...form, daily_summary_time: e.target.value })}
              data-testid="email-notify-summary-time-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="email-notify-enabled-switch" />
            Enable email notifications
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.daily_summary_enabled} onCheckedChange={(v) => setForm({ ...form, daily_summary_enabled: v })} data-testid="email-notify-summary-switch" />
            Daily morning summary
          </label>
        </div>

        {/* Customer-facing emails */}
        <div className="border rounded-lg p-4 bg-sky-50/40 border-sky-100 space-y-3" data-testid="customer-emails-panel">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <Switch checked={form.customer_emails_enabled} onCheckedChange={(v) => setForm({ ...form, customer_emails_enabled: v })} data-testid="customer-emails-switch" />
            Customer emails (sent to the client's login email)
          </label>
          {form.customer_emails_enabled && (
            <div className="flex flex-wrap gap-5 pl-1">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.customer_order_enabled} onCheckedChange={(v) => setForm({ ...form, customer_order_enabled: v })} data-testid="customer-order-email-switch" />
                Order confirmation (with items)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.customer_delivered_enabled} onCheckedChange={(v) => setForm({ ...form, customer_delivered_enabled: v })} data-testid="customer-delivered-email-switch" />
                Delivered (with items)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.customer_invoice_enabled} onCheckedChange={(v) => setForm({ ...form, customer_invoice_enabled: v })} data-testid="customer-invoice-email-switch" />
                Invoice w/ Xero PDF — Manual Invoice clients only (Direct Debit clients never get it)
              </label>
            </div>
          )}
        </div>

        <Button type="submit" disabled={saving} data-testid="email-notify-save-btn" className="bg-brand-navy hover:bg-brand-navy-hover text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Save
        </Button>
      </form>

      {/* Email log */}
      <div className="mt-5 border-t pt-3">
        <button
          type="button"
          onClick={() => { const n = !logOpen; setLogOpen(n); if (n && logRows === null) fetchLog(); }}
          className="flex items-center gap-2 text-sm font-semibold text-neutral-700 hover:text-neutral-900"
          data-testid="email-log-toggle"
        >
          <ScrollText className="w-4 h-4" />
          Email log {logRows !== null ? `(${logRows.length})` : ""} {logOpen ? "▾" : "▸"}
        </button>
        {logOpen && (
          <div className="mt-2 space-y-2" data-testid="email-log-panel">
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={fetchLog} disabled={logBusy} data-testid="email-log-refresh">
              {logBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Refresh
            </Button>
            <div className="max-h-[320px] overflow-y-auto border rounded-lg divide-y">
              {(logRows || []).map((r) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => openPreview(r.id)}
                  className="w-full text-left px-3 py-1.5 text-xs flex items-start gap-2 flex-wrap hover:bg-neutral-50"
                  title="Click to preview the email content"
                  data-testid={`email-log-row-${r.id}`}
                >
                  <span className={`font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${
                    r.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"
                  }`}>{r.status}</span>
                  <span className="text-neutral-400 shrink-0">{new Date(r.at).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  <span className="font-semibold text-sky-700 shrink-0">{r.kind}</span>
                  <span className="text-neutral-600 truncate max-w-[180px]" title={r.to}>{r.to}</span>
                  <span className="text-neutral-500 truncate flex-1 min-w-[140px]" title={r.subject}>{r.subject}{r.has_attachment ? " 📎" : ""}</span>
                  {r.error && <span className="text-red-600 w-full">{r.error}</span>}
                </button>
              ))}
              {logRows !== null && logRows.length === 0 && (
                <p className="px-3 py-3 text-xs text-muted-foreground">No emails logged yet.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Email content preview */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="email-preview-overlay">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="email-preview-dialog">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{preview.subject}</p>
                <p className="text-xs text-muted-foreground truncate">
                  To {preview.to} · {new Date(preview.at).toLocaleString("en-AU")} · {preview.kind}
                  {preview.has_attachment ? " · 📎 attachment" : ""} · {preview.status}
                </p>
              </div>
              <Button type="button" size="sm" variant="outline" className="h-7 shrink-0" onClick={() => setPreview(null)} data-testid="email-preview-close">Close</Button>
            </div>
            <div className="flex-1 overflow-auto bg-neutral-100">
              {preview.html ? (
                <iframe title="email-preview" srcDoc={preview.html} sandbox="" className="w-full h-[65vh] border-0 bg-white" />
              ) : (
                <p className="p-6 text-sm text-muted-foreground">Content wasn't stored for this entry (sent before content logging was added).</p>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
